import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox, Input } from '@xsolla/ui-core';
import { Search } from '@xsolla/ui-icons';

import { getRandomString } from '../../../utils';
import { Container, CountLabel, Header, Item, UnselectButton, Wrapper } from './customListStyle';
import { ListItems } from './types';

type Props = {
  items: ListItems;
  value: string[];
  onChange: (data: string[]) => void;
};

const CustomList = ({ items, value, onChange }: Props): JSX.Element => {
  const intl = useIntl();
  const [search, setSearch] = useState('');

  const filteredItems = useMemo(() => {
    return items.filter(
      (item) =>
        item.children && item.children.toString().toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  const handleItemClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const eventValue = event.target.value;

    if (event.target.checked) {
      onChange([...value, eventValue]);
      return;
    }

    if (value.length === 1 && value.includes(eventValue)) {
      onChange([]);
      return;
    }

    onChange([...value.filter((el) => el !== eventValue)]);
  };

  return (
    <Wrapper>
      <Header>
        <CountLabel>
          {intl.formatMessage(
            { id: 'app.multi-select.countries.selected' },
            { number: value.length, total: items.length }
          )}
        </CountLabel>
        <UnselectButton look="link" palette="muted" size="sm" onClick={() => onChange([])}>
          {intl.formatMessage({ id: 'app.multi-select.unselect' })}
        </UnselectButton>
      </Header>
      <Container>
        <Item>
          <Input
            placeholder={intl.formatMessage({ id: 'app.multi-select.search' })}
            extraAlign="left"
            extra={Search}
            onChange={(event) => setSearch(event.target.value)}
            uiSize="sm"
          />
        </Item>

        {filteredItems.length === 0 ? (
          <Item>{intl.formatMessage({ id: 'app.multi-select.empty' })}</Item>
        ) : (
          filteredItems.map((item) => {
            return (
              <Item key={getRandomString()}>
                <Checkbox
                  value={item.value}
                  checked={value.includes(item.value)}
                  data-test-id={item['data-test-id']}
                  onChange={handleItemClick}>
                  {item.children}
                </Checkbox>
              </Item>
            );
          })
        )}
      </Container>
    </Wrapper>
  );
};

export default CustomList;
